import { fetchCurrentUser } from '../../ducks/user.duck';
import { storableError } from '../../util/errors';
import {
  onCreateMerchantImage,
  onGetCategories,
  onGetMerchantImages,
  onUpdateMerchantImage,
} from '../../util/api';
import { uploadUnsignedImageToCloudinary, validateImageFile } from '../../util/cloudinaryUpload';

export const FETCH_CATEGORIES_REQUEST = 'app/StockImagesPage/FETCH_CATEGORIES_REQUEST';
export const FETCH_CATEGORIES_SUCCESS = 'app/StockImagesPage/FETCH_CATEGORIES_SUCCESS';
export const FETCH_CATEGORIES_ERROR = 'app/StockImagesPage/FETCH_CATEGORIES_ERROR';

export const FETCH_IMAGES_REQUEST = 'app/StockImagesPage/FETCH_IMAGES_REQUEST';
export const FETCH_IMAGES_SUCCESS = 'app/StockImagesPage/FETCH_IMAGES_SUCCESS';
export const FETCH_IMAGES_ERROR = 'app/StockImagesPage/FETCH_IMAGES_ERROR';

export const CREATE_IMAGE_REQUEST = 'app/StockImagesPage/CREATE_IMAGE_REQUEST';
export const CREATE_IMAGE_SUCCESS = 'app/StockImagesPage/CREATE_IMAGE_SUCCESS';
export const CREATE_IMAGE_ERROR = 'app/StockImagesPage/CREATE_IMAGE_ERROR';
export const CLEAR_CREATE_IMAGE_STATE = 'app/StockImagesPage/CLEAR_CREATE_IMAGE_STATE';

const initialState = {
  categories: [],
  images: [],
  imagesTotal: 0,
  imagesLimit: 0,
  imagesOffset: 0,
  fetchCategoriesInProgress: false,
  fetchCategoriesError: null,
  fetchImagesInProgress: false,
  fetchImagesError: null,
  createImageInProgress: false,
  createImageError: null,
  createImageSuccess: false,
};

export default function reducer(state = initialState, action = {}) {
  const { type, payload } = action;

  switch (type) {
    case FETCH_CATEGORIES_REQUEST:
      return { ...state, fetchCategoriesInProgress: true, fetchCategoriesError: null };
    case FETCH_CATEGORIES_SUCCESS:
      return { ...state, fetchCategoriesInProgress: false, categories: payload || [] };
    case FETCH_CATEGORIES_ERROR:
      return { ...state, fetchCategoriesInProgress: false, fetchCategoriesError: payload };

    case FETCH_IMAGES_REQUEST:
      return { ...state, fetchImagesInProgress: true, fetchImagesError: null };
    case FETCH_IMAGES_SUCCESS:
      return {
        ...state,
        fetchImagesInProgress: false,
        images: payload?.images || [],
        imagesTotal: payload?.total || 0,
        imagesLimit: payload?.limit || 0,
        imagesOffset: payload?.offset || 0,
      };
    case FETCH_IMAGES_ERROR:
      return { ...state, fetchImagesInProgress: false, fetchImagesError: payload };

    case CREATE_IMAGE_REQUEST:
      return { ...state, createImageInProgress: true, createImageError: null, createImageSuccess: false };
    case CREATE_IMAGE_SUCCESS:
      return { ...state, createImageInProgress: false, createImageError: null, createImageSuccess: true };
    case CREATE_IMAGE_ERROR:
      return { ...state, createImageInProgress: false, createImageError: payload, createImageSuccess: false };
    case CLEAR_CREATE_IMAGE_STATE:
      return { ...state, createImageError: null, createImageSuccess: false };
    default:
      return state;
  }
}

export const fetchCategoriesRequest = () => ({ type: FETCH_CATEGORIES_REQUEST });
export const fetchCategoriesSuccess = categories => ({ type: FETCH_CATEGORIES_SUCCESS, payload: categories });
export const fetchCategoriesError = error => ({ type: FETCH_CATEGORIES_ERROR, payload: error, error: true });

export const fetchImagesRequest = () => ({ type: FETCH_IMAGES_REQUEST });
export const fetchImagesSuccess = images => ({ type: FETCH_IMAGES_SUCCESS, payload: images });
export const fetchImagesError = error => ({ type: FETCH_IMAGES_ERROR, payload: error, error: true });

export const createImageRequest = () => ({ type: CREATE_IMAGE_REQUEST });
export const createImageSuccess = () => ({ type: CREATE_IMAGE_SUCCESS });
export const createImageError = error => ({ type: CREATE_IMAGE_ERROR, payload: error, error: true });
export const clearCreateImageState = () => ({ type: CLEAR_CREATE_IMAGE_STATE });

export const requestFetchCategories = () => dispatch => {
  dispatch(fetchCategoriesRequest());
  return onGetCategories()
    .then(categories => {
      dispatch(fetchCategoriesSuccess(Array.isArray(categories) ? categories : []));
      return categories;
    })
    .catch(error => {
      const e = storableError(error);
      dispatch(fetchCategoriesError(e));
      throw e;
    });
};

export const requestFetchMerchantImages = filters => dispatch => {
  dispatch(fetchImagesRequest());
  return onGetMerchantImages(filters)
    .then(response => {
      const payload = {
        images: Array.isArray(response?.data) ? response.data : [],
        total: Number(response?.total || 0),
        limit: Number(response?.limit || 0),
        offset: Number(response?.offset || 0),
      };
      dispatch(fetchImagesSuccess(payload));
      return payload;
    })
    .catch(error => {
      const e = storableError(error);
      dispatch(fetchImagesError(e));
      throw e;
    });
};

export const requestCreateMerchantImage = values => dispatch => {
  const { files, categoryId, subcategoryId, imageId, onProgress } = values || {};
  const maxUploadFiles = 7;
  const normalizedFiles = Array.isArray(files) ? files.filter(Boolean) : [];
  const uploadFiles = imageId ? normalizedFiles.slice(0, 1) : normalizedFiles;

  if (uploadFiles.length === 0) {
    const error = new Error('missing_file');
    dispatch(createImageError(error));
    return Promise.reject(error);
  }
  if (!imageId && uploadFiles.length > maxUploadFiles) {
    const error = new Error('too_many_files');
    dispatch(createImageError(error));
    return Promise.reject(error);
  }

  const validationError = uploadFiles
    .map(file => validateImageFile(file))
    .find(Boolean);
  if (validationError) {
    const error = new Error(validationError);
    dispatch(createImageError(error));
    return Promise.reject(error);
  }

  dispatch(createImageRequest());

  const uploadSingleImage = file =>
    uploadUnsignedImageToCloudinary(file, { folder: 'image-library/merchant' }).then(uploaded => {
      const body = {
        imageUrl: uploaded.secureUrl,
        storageKey: uploaded.publicId,
        categoryId,
        subcategoryId,
      };

      return imageId ? onUpdateMerchantImage(imageId, body) : onCreateMerchantImage(body);
    });

  const failedFiles = [];
  let successCount = 0;
  let firstError = null;

  return uploadFiles
    .reduce((promise, file, index) => {
      return promise.then(() =>
        uploadSingleImage(file)
          .then(() => {
            successCount += 1;
          })
          .catch(error => {
            failedFiles.push(file);
            if (!firstError) {
              firstError = error;
            }
          })
          .then(() => {
            if (typeof onProgress === 'function') {
              onProgress({ done: index + 1, total: uploadFiles.length });
            }
          })
      );
    }, Promise.resolve())
    .then(() => {
      if (successCount > 0) {
        dispatch(createImageSuccess());
        return { successCount, failedFiles };
      }

      throw firstError || new Error('upload_failed');
    })
    .catch(error => {
      const e = storableError(error);
      if (!successCount) {
        dispatch(createImageError(e));
      }
      throw e;
    });
};

export const loadData = () => dispatch => {
  return Promise.all([
    dispatch(fetchCurrentUser()),
    dispatch(requestFetchCategories()),
    dispatch(requestFetchMerchantImages({ limit: 12, offset: 0 })),
  ]);
};
